import React, { useEffect, useRef,  } from 'react';
import PrimaryButton from './PrimaryButton';
import { TestKitInputDescriptor } from '@components/test';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import {
	sendCommand,
	responseEmitter,
	sendAnalyzerCommand,
} from '../../utils/usb/USBConnector';
import USBToast from '../../components/toast/usb';
import { setPMBusy } from '../../utils/usb/USBConnector';
import { useConnection } from 'utils/helper/ConnectionContext';

interface ReadButtonProps {
	descriptor: TestKitInputDescriptor;
	isConnected: boolean;
	siteId: number;
	currProgress: number;
	updateProgressBar: (val: number) => void;
	updateInputValue: (descriptor: TestKitInputDescriptor, value: number) => void;
	cleanupMemory: (handler: (response: any) => void) => void;
	setShowDialog: (show: boolean) => void;
	setTimer?: (show: number | undefined) => void;
}

const ReadButton: React.FC<ReadButtonProps> = ({
    descriptor,
    isConnected,
    siteId,
    currProgress,
    updateProgressBar,
    updateInputValue,
    cleanupMemory,
    setShowDialog,
    setTimer,
}) => {
    const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const timerRef = useRef<number | undefined>(undefined);
    const handleDataRef = useRef<((response: any) => void) | null>(null);
	const handleConnectionRef = useRef<((response: any) => void) | null>(null);
	const isDoorErrorActiveRef = useRef<boolean>(false);
	const isLoggingConnectionRef = useRef(false);
	const { isConnectedEx, isPBM, serialNumber, pbmSerialNumber, firmwareNumber } = useConnection();
	const handleButtonClick = async () => {
		setPMBusy(true);
		//reset current values
		if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
		if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
		if (timerRef.current) clearInterval(timerRef.current);
		if (handleDataRef.current) {
			responseEmitter.removeListener('data', handleDataRef.current);
			cleanupMemory(handleDataRef.current);
			handleDataRef.current = null;
		}
		if (handleConnectionRef.current) {
			responseEmitter.removeListener('connection', handleConnectionRef.current);
			handleConnectionRef.current = null;
		}
		setShowDialog(true);
		const isPBM = localStorage.getItem('connectedDeviceType') === 'PBM';
		try {
			// PBM uses a timer to update progress, rather than sending back messages
			if (isPBM) {
				updateProgressBar(0);
				const startTime = Date.now();
				const intervalMs = 330;
				const maxVal = 40;

				timerRef.current = window.setInterval(() => {
					const elapsed = Date.now() - startTime;
					const val = Math.min(Math.floor(elapsed / intervalMs), maxVal);
					if (currProgress <= val) {
						updateProgressBar(val);
					}
				}, intervalMs);
			}
			setTimer?.(timerRef.current);

			const handleData = async (response: any) => {
				const trimmedResponse = response.trim();
				//console.log("Raw response:", trimmedResponse);
				//if (trimmedResponse.startsWith('05NKFE') || trimmedResponse.startsWith('0FDPM')) {console.log("PM Check connection return val");return;}
				if (trimmedResponse.startsWith('sample:')) {
					const val = parseInt(trimmedResponse.split('sample:')[1].trim());
					updateProgressBar(val + 3);
				}
				if (trimmedResponse.startsWith('test completed.')){
					updateProgressBar(40);
					addEventLog(
						'Read Data Analyzer Success', 
						'Analyzer Connection', 
						siteId, undefined,
						`Serial: ${serialNumber}, Firmware Version: ${firmwareNumber}`
					);
					setPMBusy(false);
				}
				if (trimmedResponse.startsWith('corrected count')) {
					const rluValueDec = trimmedResponse.substring(16);
					clearInterval(timerRef.current);
					clearTimeout(timeoutIdRef.current!);
					clearTimeout(cleanupTimeoutRef.current!);
					updateInputValue(descriptor, parseInt(rluValueDec));
					cleanupMemory(handleData);
					handleDataRef.current = null;
					if (handleConnectionRef.current) {
						responseEmitter.removeListener('connection', handleConnectionRef.current);
						handleConnectionRef.current = null;
					}
				} else if (
					trimmedResponse.startsWith('1AP') ||
					trimmedResponse.startsWith('AP') ||
					trimmedResponse.startsWith('P')
				) {
					const rluValueDec = trimmedResponse.substring(3, 11);
					clearInterval(timerRef.current);
					clearTimeout(timeoutIdRef.current!);
					clearTimeout(cleanupTimeoutRef.current!);
					updateInputValue(descriptor, parseInt(rluValueDec));
					cleanupMemory(handleData);
					handleDataRef.current = null;
					if (handleConnectionRef.current) {
						responseEmitter.removeListener('connection', handleConnectionRef.current);
						handleConnectionRef.current = null;
					}
					addEventLog(
						'Read Data PBM Success', 
						'PBM Connection', 
						siteId, undefined, 
						`PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`
					);
					setPMBusy(false);
				} else if (['5O2E6', '05O2E6', 'O2E6'].includes(trimmedResponse)) {
					setPMBusy(true);
					await sendCommand('04PB4');
					//will run immediately following the read, time is to allow the PM to process read
					await new Promise(resolve => setTimeout(resolve, 800));
					await sendCommand("04DA8");
				} else if (['05OEF9', '5OEF9', 'OEF9', '05NIFC', '5NIFC', '05O3E7', '5O3E7'].includes(trimmedResponse)) {
					isDoorErrorActiveRef.current = true;
					addEventLog(
						'Read Data PBM Failure - Door Opened', 
						'PBM Connection',
						siteId, undefined,
						`PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`
					);
					clearInterval(timerRef.current);
					clearTimeout(timeoutIdRef.current!);
					clearTimeout(cleanupTimeoutRef.current!);
					updateProgressBar(0);
					cleanupMemory(handleData);
					handleDataRef.current = null;
					if (handleConnectionRef.current) {
						responseEmitter.removeListener('connection', handleConnectionRef.current);
						handleConnectionRef.current = null;
					}
					USBToast("Error door open", "Only the Ready & Power lights should be lit. Fully open the PhotonMaster door, shut firmly and try again.", 'door-open-error');
					isDoorErrorActiveRef.current = false;
					setPMBusy(false);
				}
				//Currently removed due to scenario no longer occurring, might bring back 2026/06/19
				/*if(['05OEF9', '5OEF9', 'OEF9'].includes(trimmedResponse)){//Happens sometimes when the door opens or the PM is disconnected,
					// reset UI variables but don't display error as other errors are appearing
					isDoorErrorActiveRef.current = true;
					responseEmitter.on('data', delayedDoorOpenError)
					
					console.log("Delayed door open scenario");
					
					await new Promise(resolve => {
						setTimeout(resolve, 10000);
					});
					
					responseEmitter.removeListener('data', delayedDoorOpenError);
					isDoorErrorActiveRef.current = false;
				}*/
			};

			//Sometimes during door open '05OEF9', '5OEF9' is sent, but this also happens during PM disconnection
			//Listens for delayed door open signal after cleaning up values, rarely door open signal won't be sent.
			const delayedDoorOpenError = (response: any) => {
				if(!(['05NIFC', '5NIFC', 'NIFC', '05O3E7', '5O3E7', '03E7'].includes(response))){
					return
				}
				//clear one more time
				clearInterval(timerRef.current);
				clearTimeout(timeoutIdRef.current!);
				clearTimeout(cleanupTimeoutRef.current!);
				updateProgressBar(0);
				cleanupMemory(handleData);
				handleDataRef.current = null;
				if (handleConnectionRef.current) {
					responseEmitter.removeListener('connection', handleConnectionRef.current);
					handleConnectionRef.current = null;
				}
				//display UI errors & cleanup
				addEventLog(
					'Read Data PBM Failure - Door Opened', 
					'PBM Connection',
					siteId, undefined,
					`comments`
				);
				USBToast("Error door open", "Only the Ready & Power lights should be lit. Fully open the PhotonMaster door, shut firmly and try again.", 'door-open-error');
				responseEmitter.removeListener('data', delayedDoorOpenError);
				isDoorErrorActiveRef.current = false;
				setPMBusy(false);
			}

			const handleConnection = (response: any) => {
				clearInterval(timerRef.current);
				clearTimeout(timeoutIdRef.current!);
				clearTimeout(cleanupTimeoutRef.current!);
				cleanupMemory(handleData);
				handleDataRef.current = null;
				responseEmitter.removeListener('connection', handleConnection);
				handleConnectionRef.current = null;
				if(response || isDoorErrorActiveRef.current) return;
				
				if (!isLoggingConnectionRef.current) {
					isLoggingConnectionRef.current = true;
					addEventLog(
						'PBM Connection Failure', 
						'PBM Connection', 
						siteId, undefined, 
						`PBM Disconnected due to Error - PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`)
						.finally(() => { isLoggingConnectionRef.current = false; });
				}
				USBToast();
			}

			handleDataRef.current = handleData;
			handleConnectionRef.current = handleConnection;
			responseEmitter.on('data', handleData);
			responseEmitter.on('connection', handleConnection);

			if (isPBM) {
				await sendCommand('04OB3');
			} else {
				await sendAnalyzerCommand('startMeasurement\n');
			}

			// 20s hard failure timeout
			timeoutIdRef.current = setTimeout(() => {
				USBToast(undefined, undefined, "timeout-usb-connection-error");
				if (handleDataRef.current) {
					cleanupMemory(handleDataRef.current);
					responseEmitter.removeListener('data', handleDataRef.current);
					handleDataRef.current = null;
				}
				if (handleConnectionRef.current) {
					responseEmitter.removeListener('connection', handleConnectionRef.current);
					handleConnectionRef.current = null;
				}
				if(isPBM){
					addEventLog(
						'Read Data PBM Failure',
						'PBM Connection',
						siteId, undefined,
						`PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`);
				}else{
					addEventLog(
						'Read Data Analyzer Failure', 
						'Analyzer Connection', 
						siteId, undefined, 
						`Serial: ${serialNumber}, Firmware Version: ${firmwareNumber}`);
				}
				setShowDialog(false);
				setPMBusy(false);
			}, 18000);

			// 18s soft cleanup
			cleanupTimeoutRef.current = setTimeout(() => {
				clearInterval(timerRef.current);
				clearTimeout(timeoutIdRef.current!);
				if (handleDataRef.current) {
					cleanupMemory(handleDataRef.current);
					handleDataRef.current = null;
				}
				if (handleConnectionRef.current) {
					responseEmitter.removeListener('connection', handleConnectionRef.current);
					handleConnectionRef.current = null;
				}
				setPMBusy(false);
			}, 19000);

		} catch (error) {
			console.error('Failed to get response:', error);
			if(isPBM){
					addEventLog(
						'Read Data PBM Failure', 
						'PBM Connection',
						siteId, undefined,
						`PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`
					);
			}else{
					addEventLog(
						'Read Data Analyzer Failure', 
						'Analyzer Connection', 
						undefined, undefined, 
						`Serial: ${serialNumber}, Firmware Version: ${firmwareNumber}`
					);
				}
			setShowDialog(false);
			setPMBusy(false);
			clearInterval(timerRef.current);
			clearTimeout(timeoutIdRef.current!);
			clearTimeout(cleanupTimeoutRef.current!);
			if (handleDataRef.current) {
				cleanupMemory(handleDataRef.current);
				handleDataRef.current = null;
			}
			if (handleConnectionRef.current) {
				responseEmitter.removeListener('connection', handleConnectionRef.current);
				handleConnectionRef.current = null;
			}
		}
	}

    return (
        <PrimaryButton onClick={handleButtonClick} disabled={!isConnected}>
            Read
        </PrimaryButton>
    );
};

export default ReadButton;